import React, { useState, useEffect, useCallback } from 'react';
import { getPhotoUrl } from '../config/image';
import {
  Box,
  Grid,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
  Avatar,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Select,
  Input,
  Checkbox,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// window.open renvoie toujours null quand « noopener » est demandé : l'application croyait alors la fenêtre
// bloquée et ne détectait jamais sa fermeture. On ouvre sans cette option, puis on coupe le lien avec la page.
const ouvrirFenetre = (url, nom, options) => {
  const fenetre = window.open(url, nom, options);
  if (fenetre) fenetre.opener = null;
  return fenetre;
};

export default function MemberDashboard() {
  const { user } = useAuth();
  const toast = useToast();
  
  const [contributions, setContributions] = useState([]);
  const [stats, setStats] = useState({ total: 0, thisYear: 0, thisMonth: 0 });
  const [contributionTypes, setContributionTypes] = useState([]);
  const [communionRegistration, setCommunionRegistration] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [loadingCommunion, setLoadingCommunion] = useState(true);
  
  // Modals
  const { isOpen: isPaymentOpen, onOpen: onPaymentOpen, onClose: onPaymentClose } = useDisclosure();
  const { isOpen: isReceiptOpen, onOpen: onReceiptOpen, onClose: onReceiptClose } = useDisclosure();
  
  // Form states
  const [amount, setAmount] = useState('');
  const [typeId, setTypeId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [receiptYear, setReceiptYear] = useState(new Date().getFullYear() - 1);

  const calculateStats = useCallback((contribs) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    // Ne compter que les cotisations réellement payées : une tentative de
    // paiement en ligne (SumUp/Stripe/PayPal) crée une ligne 'en_attente' dès
    // le lancement, avant même que le paiement aboutisse — il ne faut jamais
    // l'inclure dans les totaux tant qu'elle n'est pas confirmée 'paye'.
    const paidContribs = contribs.filter(c => c.statut === 'paye');

    const total = paidContribs.reduce((sum, c) => sum + parseFloat(c.montant), 0);
    const thisYear = paidContribs
      .filter(c => new Date(c.date_contribution).getFullYear() === currentYear)
      .reduce((sum, c) => sum + parseFloat(c.montant), 0);
    const thisMonth = paidContribs
      .filter(c => {
        const date = new Date(c.date_contribution);
        return date.getFullYear() === currentYear && date.getMonth() === currentMonth;
      })
      .reduce((sum, c) => sum + parseFloat(c.montant), 0);

    setStats({ total, thisYear, thisMonth });
  }, []);

  const fetchContributions = useCallback(async () => {
    try {
      const response = await apiService.makeRequest(`/contributions?user_id=${user.id}`);
      const contribsData = Array.isArray(response) ? response : (response.contributions || []);
      setContributions(contribsData);
      calculateStats(contribsData);
    } catch (error) {
      console.error('Erreur chargement contributions:', error);
    }
  }, [user.id, calculateStats]);

  const fetchContributionTypes = useCallback(async () => {
    try {
      const data = await apiService.makeRequest('/contributions/types/list');
      setContributionTypes(data || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  }, []);

  const checkCommunionRegistration = useCallback(async () => {
    setLoadingCommunion(true);
    try {
      const nextCommunion = getNextCommunionDate();
      const dateStr = formatDateForMySQL(nextCommunion);
      console.log('🔍 Checking communion registration for date:', dateStr);
      
      const response = await apiService.makeRequest(
        `/communion/check-registration?user_id=${user.id}&date=${dateStr}`
      );
      console.log('📋 Communion registration response:', response);
      setCommunionRegistration(response.isRegistered || false);
    } catch (error) {
      console.error('❌ Erreur vérification inscription communion:', error);
      setCommunionRegistration(false);
    } finally {
      setLoadingCommunion(false);
    }
  }, [user.id]);

  // Premier useEffect : chargement initial des données
  useEffect(() => {
    fetchContributions();
    fetchContributionTypes();
    checkCommunionRegistration();
  }, [fetchContributions, fetchContributionTypes, checkCommunionRegistration]);
  
  // Deuxième useEffect : gestion du retour du paiement (Stripe / PayPal)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    const provider = urlParams.get('provider');
    const paypalToken = urlParams.get('token');

    const showResult = (isPaid) => {
      if (isPaid) {
        toast({
          title: '✅ Paiement réussi',
          description: 'Votre cotisation a été enregistrée avec succès',
          status: 'success',
          duration: 5000,
        });
      } else {
        toast({
          title: '❌ Paiement échoué',
          description: 'Votre paiement n\'a pas abouti — aucun montant n\'a été débité de votre compte. Vous pouvez réessayer.',
          status: 'error',
          duration: 8000,
          isClosable: true,
        });
      }
      fetchContributions();
    };

    const showError = () => {
      toast({
        title: 'Erreur de vérification',
        description: 'Impossible de vérifier votre paiement. Contactez l\'administration.',
        status: 'error',
        duration: 6000,
      });
    };

    if (payment === 'success' && sessionId) {
      window.history.replaceState({}, document.title, '/dashboard');
      apiService.makeRequest(`/contributions/payment/stripe/verify/${sessionId}`)
        .then((data) => showResult(data.is_paid))
        .catch(showError);
    } else if (payment === 'success' && provider === 'paypal' && paypalToken) {
      window.history.replaceState({}, document.title, '/dashboard');
      apiService.makeRequest('/contributions/payment/paypal/capture', {
        method: 'POST',
        body: JSON.stringify({ orderId: paypalToken }),
      })
        .then((data) => showResult(data.is_paid))
        .catch(showError);
    } else if (payment === 'cancelled') {
      toast({
        title: 'Paiement annulé',
        description: 'Vous avez annulé le paiement',
        status: 'info',
        duration: 3000,
      });
      
      window.history.replaceState({}, document.title, '/dashboard');
    }
  }, [fetchContributions, toast]);

  
  const getNextCommunionDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let year = today.getFullYear();
    let month = today.getMonth();
    
    // Trouver le premier jour du mois
    let date = new Date(year, month, 1);
    
    // Trouver le premier dimanche
    while (date.getDay() !== 0) {
      date.setDate(date.getDate() + 1);
    }
    
    // Ajouter 7 jours pour avoir le deuxième dimanche
    date.setDate(date.getDate() + 7);
    
    // Si cette date est passée, passer au mois suivant
    if (date <= today) {
      month++;
      if (month > 11) {
        month = 0;
        year++;
      }
      
      date = new Date(year, month, 1);
      
      // Trouver le premier dimanche du mois suivant
      while (date.getDay() !== 0) {
        date.setDate(date.getDate() + 1);
      }
      
      // Ajouter 7 jours pour le deuxième dimanche
      date.setDate(date.getDate() + 7);
    }
    
    return date;
  };

  /**
   * Formate une date en YYYY-MM-DD pour MySQL
   */
  const formatDateForMySQL = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };


  const handleCommunionRegistration = async () => {
    setIsRegistering(true);
    try {
      const nextCommunion = getNextCommunionDate();
      const dateStr = formatDateForMySQL(nextCommunion);
      
      if (communionRegistration) {
        // Annuler l'inscription
        console.log('🗑️ Unregistering from:', dateStr);
        await apiService.makeRequest('/communion/unregister', {
          method: 'DELETE',
          body: JSON.stringify({
            user_id: user.id,
            date_communion: dateStr
          })
        });
        setCommunionRegistration(false);
        toast({
          title: 'Inscription annulée',
          description: 'Votre participation à la Sainte Cène a été annulée',
          status: 'info',
          duration: 3000,
        });
      } else {
        // S'inscrire
        console.log('✅ Registering for:', dateStr);
        await apiService.makeRequest('/communion/register', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user.id,
            date_communion: dateStr
          })
        });
        setCommunionRegistration(true);
        toast({
          title: 'Inscription confirmée',
          description: 'Votre participation à la Sainte Cène a été enregistrée',
          status: 'success',
          duration: 3000,
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de traiter votre inscription',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsRegistering(false);
    }
  };
  
// Remplacer handlePayment dans MemberDashboard.js avec cette version améliorée

  const handlePayment = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: 'Erreur',
        description: 'Veuillez entrer un montant valide',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!typeId) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner un type de cotisation',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      // Récupérer le nom du type de cotisation
      const selectedType = contributionTypes.find(t => t.id === parseInt(typeId));
      
      let response;
      
      if (paymentMethod === 'sumup') {
        // Pour SumUp : processus en 2 étapes
        // 1. Créer d'abord la contribution
        const contributionResponse = await apiService.makeRequest('/contributions', {
          method: 'POST',
          body: JSON.stringify({
            montant: parseFloat(amount),
            type_id: parseInt(typeId),
            statut: 'en_attente',
            mode_paiement: 'sumup',
          }),
        });
        
        const contributionId = contributionResponse.contribution?.id || contributionResponse.id;
        
        if (!contributionId) {
          throw new Error('Erreur lors de la création de la cotisation');
        }
        
        // 2. Créer le checkout SumUp avec le contribution_id
        const returnUrl = `${window.location.origin}/dashboard`;
        
        response = await apiService.makeRequest('/payments/sumup/create-checkout', {
          method: 'POST',
          body: JSON.stringify({
            contribution_id: parseInt(contributionId),
            return_url: returnUrl,
          }),
        });
      } else {
        // Pour Stripe/PayPal : utiliser l'ancien endpoint unifié
        response = await apiService.makeRequest('/contributions/payment/create', {
          method: 'POST',
          body: JSON.stringify({
            amount: parseFloat(amount),
            type_id: parseInt(typeId),
            type_name: selectedType?.nom || 'Cotisation',
            paymentMethod: paymentMethod,
          }),
        });
      }

      if (paymentMethod === 'sumup') {
        // Ouvrir popup SumUp
        const width = 800;
        const height = 900;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popupFeatures = `
          width=${width},
          height=${height},
          left=${left},
          top=${top},
          toolbar=no,
          location=no,
          directories=no,
          status=no,
          menubar=no,
          scrollbars=yes,
          resizable=yes`.replace(/\s+/g, '');
        
        // Page interne avec le widget SumUp (SumUp n'a pas de page hébergée
        // accessible par simple redirection — le paiement carte doit se
        // faire via leur SDK JS, intégré dans notre propre page).
        const sumupUrl = `${window.location.origin}/sumup-checkout?checkout_id=${response.checkout_id}`;
        const sumupWindow = ouvrirFenetre(sumupUrl, 'SumUpPayment', popupFeatures);
        
        if (sumupWindow) {
          sumupWindow.focus();
          
          toast({
            title: '💳 Paiement SumUp',
            description: 'Une fenêtre sécurisée s\'est ouverte pour votre paiement',
            status: 'info',
            duration: 5000,
          });
          
          onPaymentClose();
          
          const checkInterval = setInterval(() => {
            if (sumupWindow.closed) {
              clearInterval(checkInterval);
              
              setTimeout(() => {
                fetchContributions();
                toast({
                  title: '🔄 Actualisation',
                  description: 'Vérification de votre paiement...',
                  status: 'info',
                  duration: 3000,
                });
              }, 2000);
            }
          }, 500);
          
          setTimeout(() => {
            if (!sumupWindow.closed) {
              clearInterval(checkInterval);
            }
          }, 600000);
          
        } else {
          toast({
            title: '⚠️ Popup bloquée',
            description: (
              <div>
                <p>Votre navigateur bloque les popups.</p>
                <p style={{marginTop: '8px'}}>
                  Veuillez autoriser les popups pour ce site ou 
                  <a href={sumupUrl} target="_blank" rel="noopener noreferrer" 
                     style={{color: '#3182ce', textDecoration: 'underline', marginLeft: '4px'}}>
                    cliquez ici pour payer
                  </a>
                </p>
              </div>
            ),
            status: 'warning',
            duration: 10000,
            isClosable: true,
          });
        }
      } else if (paymentMethod === 'stripe') {
        // Créer une popup centrée avec dimensions personnalisées
        const width = 800;
        const height = 900;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popupFeatures = `
          width=${width},
          height=${height},
          left=${left},
          top=${top},
          toolbar=no,
          location=no,
          directories=no,
          status=no,
          menubar=no,
          scrollbars=yes,
          resizable=yes`.replace(/\s+/g, '');
        
        const stripeWindow = ouvrirFenetre(response.url, 'StripePayment', popupFeatures);
        
        if (stripeWindow) {
          // Mettre le focus sur la popup
          stripeWindow.focus();
          
          toast({
            title: '💳 Paiement Stripe',
            description: 'Une fenêtre sécurisée s\'est ouverte pour votre paiement',
            status: 'info',
            duration: 5000,
          });
          
          // Fermer le modal de sélection
          onPaymentClose();
          
          // Surveiller la fermeture de la popup
          const checkInterval = setInterval(() => {
            if (stripeWindow.closed) {
              clearInterval(checkInterval);
              
              // Attendre un peu que le webhook traite le paiement
              setTimeout(() => {
                fetchContributions();
                toast({
                  title: '🔄 Actualisation',
                  description: 'Vérification de votre paiement...',
                  status: 'info',
                  duration: 3000,
                });
              }, 2000);
            }
          }, 500);
          
          // Timeout de sécurité (10 minutes)
          setTimeout(() => {
            if (!stripeWindow.closed) {
              clearInterval(checkInterval);
            }
          }, 600000);
          
        } else {
          // Si la popup est bloquée
          toast({
            title: '⚠️ Popup bloquée',
            description: (
              <div>
                <p>Votre navigateur bloque les popups.</p>
                <p style={{marginTop: '8px'}}>
                  Veuillez autoriser les popups pour ce site ou 
                  <a href={response.url} target="_blank" rel="noopener noreferrer" 
                     style={{color: '#3182ce', textDecoration: 'underline', marginLeft: '4px'}}>
                    cliquez ici pour payer
                  </a>
                </p>
              </div>
            ),
            status: 'warning',
            duration: 10000,
            isClosable: true,
          });
        }
        
      } else if (paymentMethod === 'paypal') {
        // Même approche pour PayPal
        const width = 800;
        const height = 900;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popupFeatures = `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes`;
        
        ouvrirFenetre(response.approvalUrl, 'PayPalPayment', popupFeatures);
        onPaymentClose();
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

// Remplacer la fonction downloadAnnualReceipt dans MemberDashboard.js
// (vers la ligne 310)

  const downloadAnnualReceipt = async () => {
    try {
      console.log('🔍 Downloading receipt for year:', receiptYear);
      console.log('📋 User matricule:', user.matricule);
      
      // CORRECTION: utiliser /download à la fin de l'URL
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/contributions/fiscal-receipt/${receiptYear}/download`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        }
      );
      
      console.log('📡 Response status:', response.status);
      console.log('📄 Content-Type:', response.headers.get('content-type'));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        let serverMessage = '';
        try { serverMessage = JSON.parse(errorText).error || ''; } catch (e) { /* réponse non JSON */ }
        throw new Error(serverMessage || `Erreur lors du téléchargement du reçu (${response.status})`);
      }
      
      const blob = await response.blob();
      console.log('📦 Blob received:', blob.size, 'bytes, type:', blob.type);
      
      if (blob.size === 0) {
        throw new Error('Le fichier PDF est vide');
      }
      
      // Créer l'URL du blob
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `recu_fiscal_${receiptYear}_${user.matricule}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Nettoyage
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(a);
      }, 100);
      
      toast({
        title: 'Succès',
        description: 'Reçu fiscal téléchargé',
        status: 'success',
        duration: 3000,
      });
      
      onReceiptClose();
    } catch (error) {
      console.error('❌ Error downloading receipt:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de télécharger le reçu',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getChartData = () => {
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    const currentYear = new Date().getFullYear();
    const monthlyData = new Array(12).fill(0);

    contributions
      .filter(c => c.statut === 'paye' && new Date(c.date_contribution).getFullYear() === currentYear)
      .forEach(c => {
        const month = new Date(c.date_contribution).getMonth();
        monthlyData[month] += parseFloat(c.montant);
      });

    return {
      labels: months,
      datasets: [{
        label: `Cotisations ${currentYear}`,
        data: monthlyData,
        borderColor: 'rgb(45, 198, 83)',
        backgroundColor: 'rgba(45, 198, 83, 0.1)',
        tension: 0.3,
      }],
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date invalide';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date invalide';
    return date.toLocaleDateString('fr-FR');
  };

  const communionDate = getNextCommunionDate();
  const canRegisterForCommunion = new Date() < new Date(communionDate.getTime() - 24 * 60 * 60 * 1000);
  
  // Années disponibles pour le reçu fiscal (exclut l'année en cours)
  const availableYears = Array.from(
    { length: 5 }, 
    (_, i) => new Date().getFullYear() - 1 - i
  );

  return (
    <Box>
      <Heading size="lg" mb={6}>Mon Tableau de Bord</Heading>

      {/* Carte de membre */}
      <Card
        mb={6}
        bgGradient="linear(135deg, green.400, green.600 60%, green.700)"
        color="white"
        position="relative"
        overflow="hidden"
        boxShadow="0 4px 12px rgba(31,112,64,0.35), inset 0 1px 0 rgba(255,255,255,0.2)"
      >
        {/* Reflet brillant diagonal */}
        <Box
          position="absolute"
          top="-50%"
          left="-20%"
          width="60%"
          height="200%"
          bgGradient="linear(115deg, whiteAlpha.300, whiteAlpha.0)"
          transform="rotate(15deg)"
          pointerEvents="none"
        />
        <CardBody position="relative">
          <Grid
            templateColumns={{ base: '1fr', sm: 'auto 1fr' }}
            gap={6}
            justifyItems={{ base: 'center', sm: 'start' }}
            textAlign={{ base: 'center', sm: 'left' }}
          >
            <Avatar
              size="2xl"
              name={`${user.prenom} ${user.nom}`}
              src={user.photo ? getPhotoUrl(user.photo) : undefined}
            />
            <VStack align={{ base: 'center', sm: 'start' }} justify="center" spacing={1}>
              <HStack>
                <img src="/logo-eecf.png" alt="EECF" style={{ height: '40px' }} />
                <Text fontSize="xl" fontWeight="bold">EECF Paris</Text>
              </HStack>
              <Text fontSize="2xl" fontWeight="bold" mt={2}>
                {user.prenom} {user.nom}
              </Text>
              <HStack spacing={4} mt={2} flexWrap="wrap" justify={{ base: 'center', sm: 'start' }}>
                <VStack align={{ base: 'center', sm: 'start' }} spacing={0}>
                  <Text fontSize="xs" opacity={0.8}>Matricule</Text>
                  <Text fontWeight="bold">{user.matricule}</Text>
                </VStack>
                <VStack align={{ base: 'center', sm: 'start' }} spacing={0}>
                  <Text fontSize="xs" opacity={0.8}>N° Carte</Text>
                  <Text fontWeight="bold">{user.numero_carte || '-'}</Text>
                </VStack>
                <VStack align={{ base: 'center', sm: 'start' }} spacing={0}>
                  <Text fontSize="xs" opacity={0.8}>Membre depuis</Text>
                  <Text fontWeight="bold">
                    {new Date(user.date_adhesion).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </Text>
                </VStack>
              </HStack>
            </VStack>
          </Grid>
        </CardBody>
      </Card>

      {/* Statistiques */}
      <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6} mb={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total des cotisations</StatLabel>
              <StatNumber>{stats.total.toFixed(2)} €</StatNumber>
              <StatHelpText>Depuis votre adhésion</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Cette année</StatLabel>
              <StatNumber>{stats.thisYear.toFixed(2)} €</StatNumber>
              <StatHelpText>{new Date().getFullYear()}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Ce mois</StatLabel>
              <StatNumber>{stats.thisMonth.toFixed(2)} €</StatNumber>
              <StatHelpText>{new Date().toLocaleDateString('fr-FR', { month: 'long' })}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </Grid>

      {/* Graphique annuel */}
      <Card mb={6}>
        <CardBody>
          <Heading size="md" mb={4}>Évolution de mes cotisations {new Date().getFullYear()}</Heading>
          <Line data={getChartData()} options={{ responsive: true, maintainAspectRatio: true }} />
        </CardBody>
      </Card>

      {/* Sainte Cène */}
      <Card mb={6}>
        <CardBody>
          <Heading size="md" mb={4}>Sainte Cène</Heading>
          {loadingCommunion ? (
            <Flex align="center" gap={3}>
              <Spinner size="sm" />
              <Text color="gray.600">Vérification de votre inscription...</Text>
            </Flex>
          ) : canRegisterForCommunion ? (
            <VStack align="start" spacing={3}>
              <Checkbox
                isChecked={communionRegistration}
                onChange={handleCommunionRegistration}
                isDisabled={isRegistering}
                colorScheme="green"
              >
                Oui, je participe à la Sainte Cène du {communionDate.toLocaleDateString('fr-FR')}
              </Checkbox>
              {communionRegistration && (
                <Badge colorScheme="green">✓ Votre participation est confirmée</Badge>
              )}
            </VStack>
          ) : (
            <Text color="gray.600">
              La prochaine Sainte Cène aura lieu le {communionDate.toLocaleDateString('fr-FR')}. 
              L'inscription ouvrira le 1er du mois.
            </Text>
          )}
        </CardBody>
      </Card>

      {/* Actions rapides */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
        <Card>
          <CardBody>
            <Heading size="md" mb={4}>Mes cotisations</Heading>
            <VStack spacing={3}>
              {/* Statistiques rapides */}
              <HStack justify="space-between" width="full" p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="medium">Total {new Date().getFullYear()}</Text>
                <Badge colorScheme="green" fontSize="md">{stats.thisYear.toFixed(2)} €</Badge>
              </HStack>
              
              {/* Section Paiement rapide */}
              <Box width="full" pt={2}>
                <Heading size="sm" mb={2}>Paiement en ligne rapide</Heading>
                <Text fontSize="xs" color="gray.600" mb={3}>
                  Payez rapidement et en toute sécurité avec Stripe
                </Text>
                <Button 
                  colorScheme="blue" 
                  width="full"
                  leftIcon={<Text>💳</Text>}
                  onClick={() => {
                    setPaymentMethod('stripe');
                    onPaymentOpen();
                  }}
                >
                  Payer avec Stripe
                </Button>
              </Box>
              
              {/* Autres options */}
              <Button 
                colorScheme="green" 
                variant="outline"
                width="full" 
                onClick={onPaymentOpen}
                size="sm"
              >
                Autres moyens de paiement
              </Button>
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Heading size="md" mb={4}>Mes dernières cotisations</Heading>
            <VStack align="start" spacing={2}>
              {contributions.filter(c => c.statut === 'paye').slice(0, 3).map(c => (
                <HStack key={c.id} justify="space-between" width="full">
                  <Text fontSize="sm">{formatDate(c.date_contribution)}</Text>
                  <Badge colorScheme="green">{c.montant} €</Badge>
                </HStack>
              ))}
              {contributions.filter(c => c.statut === 'paye').length === 0 && (
                <Text fontSize="sm" color="gray.500">Aucune cotisation enregistrée</Text>
              )}
            </VStack>
          </CardBody>
        </Card>
      </Grid>

      {/* Modal de paiement */}
      <Modal isOpen={isPaymentOpen} onClose={onPaymentClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Effectuer une cotisation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Type de cotisation</FormLabel>
                <Select value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                  <option value="">Sélectionner...</option>
                  {contributionTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.nom}
                      {type.montant_suggere && ` (Suggéré: ${type.montant_suggere}€)`}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Montant (€)</FormLabel>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="50"
                  min="1"
                  step="0.01"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Méthode de paiement</FormLabel>
                <Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                  <option value="stripe">💳 Carte bancaire (Stripe)</option>
                  <option value="paypal">💰 PayPal</option>
                  <option value="sumup">💳 SumUp</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPaymentClose}>
              Annuler
            </Button>
            <Button colorScheme="green" onClick={handlePayment}>
              Payer {amount ? `${amount} €` : ''}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal téléchargement reçu fiscal */}
      <Modal isOpen={isReceiptOpen} onClose={onReceiptClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Télécharger mon reçu fiscal</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Sélectionnez l'année fiscale pour laquelle vous souhaitez télécharger votre reçu.
                Le reçu de l'année en cours n'est pas encore disponible.
              </Text>
              <FormControl isRequired>
                <FormLabel>Année fiscale</FormLabel>
                <Select 
                  value={receiptYear} 
                  onChange={(e) => setReceiptYear(parseInt(e.target.value))}
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onReceiptClose}>
              Annuler
            </Button>
            <Button colorScheme="blue" onClick={downloadAnnualReceipt}>
              Télécharger
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}